import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import AccountsConstants from "../../common/AccountsConstants";
import {
  formatDate,
  formatINR,
  monthStartInput,
  todayInput,
} from "../accounts/accountUser";
import LedgerCombo from "../accounts/LedgerCombo";
import PageShell from "../accounts/PageShell";
import useAccountMeta from "../accounts/useAccountMeta";
import CollapsibleSection from "../../Components/CollapsibleSection/CollapsibleSection";
import EntryPreview from "../../Components/Accounts/EntryPreview";
import useDocNav, { DocChip } from "./useDocNav";
import "../accounts/accounts.css";
import "./flow.css";
import { useSelector } from "react-redux";

/* Phones start with filters closed so the list is visible first. */
const isPhone = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(max-width: 700px)").matches;

/* Receipts and payments together: pending approval, rejected, posted and cancelled. Approvers approve or reject here. */

const TONE = {
  PENDING: "warn",
  APPROVED: "ok",
  REJECTED: "bad",
  CANCELLED: "bad",
};
const TYPE_LABEL = { RECEIPT: "Receipt", PAYMENT: "Payment" };

export function sourceRefOf(row) {
  const source = row && row.source;
  if (!source) return "";
  if (source.type === "INVOICE")
    return [source.invoiceNr, source.loadingAdviceNr, source.orderNo]
      .filter(Boolean)
      .join(" / ");
  if (source.type === "LOADING_ADVICE")
    return [source.loadingAdviceNr, source.orderNo].filter(Boolean).join(" / ");
  if (source.type === "ORDER") return source.orderNo || "";
  return source.documentNo || "";
}

/* Links to the customer-app documents an entry was raised against. */
export function AllocationDocs({ allocations, advance = 0, advanceUsed = 0 }) {
  const nav = useDocNav();
  const lines = allocations || [];
  if (!lines.length) return null;
  return (
    <div className="flow-alloc">
      {lines.map((line) => (
        <div className="flow-alloc-line" key={line.invoiceNr}>
          <DocChip
            label="Invoice"
            no={`${line.invoiceNr} ${formatINR(line.amount)}`}
            onClick={() => nav.openInvoice(line.invoiceNr)}
          />
          {line.loadingAdviceNr && (
            <DocChip
              label="Loading"
              no={line.loadingAdviceNr}
              onClick={() => nav.openLoadingAdvice(line.loadingAdviceNr)}
            />
          )}
          {line.orderNo && (
            <DocChip
              label="Order"
              no={line.orderNo}
              onClick={() => nav.openOrder(line.orderNo)}
            />
          )}
        </div>
      ))}
      {Number(advance) > 0 && (
        <span
          className="flow-advance"
          title="Received, not applied to an invoice"
        >
          <i className="bi bi-wallet2" aria-hidden="true" />
          <span>Advance</span>
          <strong>{formatINR(advance)}</strong>
        </span>
      )}
      {Number(advanceUsed) > 0 && (
        <span
          className="flow-advance from-advance"
          title="Taken from advance already on the customer. Not received again."
        >
          <i className="bi bi-wallet2" aria-hidden="true" />
          <span>From advance</span>
          <strong>{formatINR(advanceUsed)}</strong>
        </span>
      )}
    </div>
  );
}

export function SourceDocs({
  source,
  compact = false,
  lead = "Against",
  hideInvoice = false,
}) {
  const nav = useDocNav();
  if (!source) return null;
  const links = [
    !hideInvoice &&
      source.invoiceNr && {
        label: "Invoice",
        no: source.invoiceNr,
        open: () => nav.openInvoice(source.invoiceNr),
      },
    source.loadingAdviceNr && {
      label: "Loading",
      no: source.loadingAdviceNr,
      open: () => nav.openLoadingAdvice(source.loadingAdviceNr),
    },
    source.orderNo && {
      label: "Order",
      no: source.orderNo,
      open: () => nav.openOrder(source.orderNo),
    },
    source.documentNo && {
      label: "Purchase",
      no: source.documentNo,
      open: null,
    },
  ].filter(Boolean);
  if (!links.length) return null;
  if (compact) {
    return (
      <div className="flow-doc-links">
        {links.map((link) => (
          <DocChip
            key={link.no}
            label={link.label}
            no={link.no}
            onClick={link.open}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="accounts-note ok flow-source">
      <span>{lead}</span>
      {links.map((link) => (
        <DocChip
          key={link.no}
          label={link.label}
          no={link.no}
          onClick={link.open}
        />
      ))}
    </div>
  );
}

export function ApprovalPanel({ row, onDone, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const decide = async (decision) => {
    if (decision === "reject" && !reason.trim()) {
      setError("Enter the reason for rejecting");
      return;
    }
    setBusy(decision);
    setError("");
    try {
      if (decision === "approve")
        await accountsFlowApi.approve(row.id, reason.trim());
      else await accountsFlowApi.reject(row.id, reason.trim());
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="accounts-panel accounts-cancel">
      <div>
        <h3>
          {row.requestNo} · {TYPE_LABEL[row.voucherType]}{" "}
          {formatINR(row.amount)}
        </h3>
        <p className="accounts-hint">
          {row.particulars}
          {sourceRefOf(row) ? ` · against ${sourceRefOf(row)}` : ""}
          {row.submittedByName
            ? ` · entered by ${row.submittedByName}`
            : ""}.{" "}
          {Number(row.advanceUsed) > 0 && !row.amount
            ? "Approving applies the customer's advance to these invoices. The bank is not posted again."
            : `Approving posts it to the ledger${row.voucherType === "RECEIPT" && ((row.allocations || []).length || row.source) ? " and updates each allocated invoice" : ""}${Number(row.advanceUsed) > 0 ? `. ${formatINR(row.advanceUsed)} is taken from advance already received` : ""}.`}
        </p>
        {(row.allocations || []).length > 0 && (
          <AllocationDocs
            allocations={row.allocations}
            advance={row.advance}
            advanceUsed={row.advanceUsed}
          />
        )}
        {row.voucherType === "RECEIPT" &&
          !(row.allocations || []).length &&
          !(row.source && row.source.invoiceNr) && (
            <p className="accounts-hint">
              No invoice is attached. Approving credits the customer only. No
              invoice will be marked paid.
            </p>
          )}
      </div>
      <label className="tally-field">
        Remark (required to reject)
        <textarea
          className="form-control"
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      {error && <div className="accounts-error">{error}</div>}
      <div className="accounts-cancel-actions">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onClose}
          disabled={Boolean(busy)}
        >
          Close
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          onClick={() => decide("reject")}
          disabled={Boolean(busy)}
        >
          {busy === "reject" ? "Rejecting…" : "Reject"}
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => decide("approve")}
          disabled={Boolean(busy)}
        >
          {busy === "approve" ? "Approving…" : "Approve"}
        </button>
      </div>
    </div>
  );
}

export default function FlowVoucherList() {
  const navigate = useNavigate();
  const { meta } = useAccountMeta();
  const [flowMeta, setFlowMeta] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const blankFilters = () => ({
    voucherType: "RECEIPT,PAYMENT",
    status: "",
    fromDate: monthStartInput(),
    toDate: todayInput(),
    ledgerId: "",
    search: "",
  });
  const [filters, setFilters] = useState(blankFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState("");
  const [deciding, setDeciding] = useState(null);
  const loadSeq = useRef(0);
  let userData = useSelector((state) => state?.user?.data);
  const [userRoleData, setUserRoleData] = useState(false);

  useEffect(() => {
    const roles = userData?.roles || [];

    const isSalesOfficer = roles.some(
      (role) => role.roleId === "SALES-OFFICER",
    );

    setUserRoleData(isSalesOfficer);
  }, [userData]);
  const load = async (next = filters) => {
    const seq = ++loadSeq.current;
    setError("");
    setLoading(true);
    try {
      const result = await accountsFlowApi.entries({
        ...next,
        search: String(next.search || "").trim(),
      });
      if (seq !== loadSeq.current) return;
      setRows(result?.data?.items || []);
      setPendingCount(result?.data?.pendingCount || 0);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(err.message);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    accountsFlowApi
      .meta()
      .then((result) => setFlowMeta(result?.data || null))
      .catch(() => {});
    accountsApi
      .ledgers()
      .then((result) => setLedgers(result?.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (next) => {
    setFilters(next);
    load(next);
  };
  const setFilter = (key) => (event) =>
    apply({ ...filters, [key]: event.target.value });
  const canApprove = Boolean(flowMeta?.canApprove);
  const open = (row) => {
    if (row.kind === "request")
      navigate(`${AccountsFlowConstants.ENTRY_VIEW}?id=${row.id}`);
    else if (row.requestId)
      navigate(`${AccountsFlowConstants.ENTRY_VIEW}?id=${row.requestId}`);
    else navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${row.voucherId}`);
  };

  return (
    <PageShell
      title="Receipts & Payments"
      subtitle={
        !flowMeta
          ? ""
          : canApprove
            ? `${pendingCount} waiting for your approval`
            : "Your entries are posted once an admin approves them"
      }
      actions={
        <>
          <button
            type="button"
            className="btn btnTable"
            onClick={() => navigate(AccountsFlowConstants.RECEIPT_ENTRY)}
          >
            <i className="bi bi-box-arrow-in-down" /> New Receipt
          </button>
          {!userRoleData && (
            <button
              type="button"
              className="btn btnTable"
              onClick={() => navigate(AccountsFlowConstants.PAYMENT_ENTRY)}
            >
              <i className="bi bi-box-arrow-up-right" /> New Payment
            </button>
          )}
        </>
      }
    >
      <CollapsibleSection
        title="Filters"
        defaultOpen={!isPhone()}
        meta={pendingCount ? `${pendingCount} waiting for approval` : ""}
      >
        <div className="accounts-panel accounts-filter-grid">
          <label>
            Type
            <select
              className="form-select"
              value={filters.voucherType}
              onChange={setFilter("voucherType")}
            >
              <option value="RECEIPT,PAYMENT">Receipts &amp; payments</option>
              <option value="RECEIPT">Receipts</option>
              <option value="PAYMENT">Payments</option>
            </select>
          </label>
          <label>
            Status
            <select
              className="form-select"
              value={filters.status}
              onChange={setFilter("status")}
            >
              <option value="">All statuses</option>
              <option value="PENDING">Pending approval</option>
              <option value="APPROVED">Posted</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </label>
          <label>
            From date
            <input
              className="form-control"
              type="date"
              value={filters.fromDate}
              onChange={setFilter("fromDate")}
            />
          </label>
          <label>
            To date
            <input
              className="form-control"
              type="date"
              value={filters.toDate}
              onChange={setFilter("toDate")}
            />
          </label>
          <LedgerCombo
            label="Ledger"
            placeholder={meta?.ownCustomersOnly ? "Your customers" : "All ledgers"}
            groups={meta?.ledgerGroups || []}
            ledgers={meta?.ownCustomersOnly ? ledgers.filter((row) => row.group === "SUNDRY_DEBTOR") : ledgers}
            value={filters.ledgerId}
            onChange={(id) => apply({ ...filters, ledgerId: id })}
          />
          <label>
            Search
            <input
              className="form-control"
              value={filters.search}
              placeholder="Voucher, reference, loading advice or order no"
              onChange={setFilter("search")}
            />
          </label>
          <button
            type="button"
            className="btn btn-outline-warning"
            onClick={() =>
              apply({
                ...blankFilters(),
                status: "PENDING",
                fromDate: "",
                toDate: "",
              })
            }
          >
            Pending approval ({pendingCount})
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => apply(blankFilters())}
          >
            Clear filters
          </button>
        </div>
      </CollapsibleSection>
      {error && <div className="accounts-error">{error}</div>}
      {deciding && (
        <ApprovalPanel
          row={deciding}
          onClose={() => setDeciding(null)}
          onDone={() => {
            setDeciding(null);
            load();
          }}
        />
      )}
      <div className="accounts-table-toolbar">
        <span>
          {loading
            ? "Loading vouchers…"
            : `${rows.length} voucher${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="accounts-table-wrap">
        <table className="accounts-table flow-cards">
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher No</th>
              <th>Type</th>
              <th>Particulars</th>
              <th>Reference</th>
              <th className="num">Amount</th>
              <th>Status</th>
              <th>Entered by</th>
              <th>Next step</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.kind}-${row.id}`}>
                <td data-label="Date">{formatDate(row.voucherDate)}</td>
                <td data-label="Voucher No">
                  <div className="flow-cell">
                    <button
                      type="button"
                      className="accounts-voucher-link"
                      onClick={() => open(row)}
                    >
                      <span>{row.voucherNo || row.requestNo}</span>
                    </button>
                    {row.voucherNo && row.requestNo && (
                      <div className="accounts-hint">{row.requestNo}</div>
                    )}
                  </div>
                </td>
                <td data-label="Type">
                  {TYPE_LABEL[row.voucherType] || row.voucherType}
                </td>
                <td data-label="Particulars">{row.particulars}</td>
                <td data-label="Reference">
                  <div className="flow-cell">
                    {row.referenceNo && !(row.allocations || []).length && (
                      <div>{row.referenceNo}</div>
                    )}
                    {(row.allocations || []).length > 0 ? (
                      <AllocationDocs
                        allocations={row.allocations}
                        advance={row.advance}
                        advanceUsed={row.advanceUsed}
                      />
                    ) : (
                      row.source && <SourceDocs source={row.source} compact />
                    )}
                  </div>
                </td>
                <td className="num" data-label="Amount">
                  <div className="flow-cell">
                    {formatINR(row.amount)}
                    {Number(row.advanceUsed) > 0 ? (
                      <div className="accounts-hint">
                        + {formatINR(row.advanceUsed)} advance
                      </div>
                    ) : null}
                  </div>
                </td>
                <td data-label="Status">
                  <span
                    className={`accounts-chip ${TONE[row.status] || "muted"}`}
                  >
                    {row.statusLabel}
                  </span>
                </td>
                <td data-label="Entered by">
                  <div className="flow-cell">
                    {row.submittedByName}
                    {row.approvedByName && row.status === "APPROVED" ? (
                      <div className="accounts-hint">
                        approved by {row.approvedByName}
                      </div>
                    ) : null}
                  </div>
                </td>
                <td className="accounts-next" data-label="Next step">
                  {row.status === "PENDING" && canApprove && (
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => setDeciding(row)}
                    >
                      Approve / Reject
                    </button>
                  )}
                  {row.status === "PENDING" && !canApprove && (
                    <span className="accounts-hint">Waiting for admin</span>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => open(row)}
                  >
                    <i className="bi bi-eye" />
                  </button>
                </td>
              </tr>
            ))}
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="accounts-hint">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9}>
                  No receipts or payments match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}

/* One entry (request) with its approval state; a posted one links to the Accounts voucher. */
export function FlowEntryView() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [row, setRow] = useState(null);
  const [flowMeta, setFlowMeta] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState("");
  const load = () =>
    accountsFlowApi
      .entry(id)
      .then((result) => setRow(result.data))
      .catch((err) => setError(err.message));
  useEffect(() => {
    load();
    accountsFlowApi
      .meta()
      .then((result) => setFlowMeta(result?.data || null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!row || !row.requestNo) {
    return (
      <PageShell title="Receipt / Payment">
        <div className={error ? "accounts-error" : ""}>
          {error || "Loading..."}
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary mt-2"
          onClick={() => navigate(AccountsFlowConstants.VOUCHERS)}
        >
          Back to list
        </button>
      </PageShell>
    );
  }
  return (
    <PageShell
      title={`${TYPE_LABEL[row.voucherType]} ${row.voucherNo || row.requestNo}`}
    >
      <div className="accounts-form-actions start">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate(AccountsFlowConstants.VOUCHERS)}
        >
          Back to list
        </button>
        {row.status === "PENDING" && flowMeta?.canApprove && (
          <button
            type="button"
            className="btn btn-success"
            onClick={() => setDeciding(true)}
          >
            Approve / Reject
          </button>
        )}
        {row.voucherId && (
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() =>
              navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${row.voucherId}`)
            }
          >
            Open voucher {row.voucherNo}
          </button>
        )}
      </div>
      {row.status === "PENDING" && (
        <div className="accounts-note warn">
          Waiting for admin approval
          {row.submittedByName ? ` (entered by ${row.submittedByName})` : ""}.
          Not in the ledger until approved.
        </div>
      )}
      {row.status === "REJECTED" && (
        <div className="accounts-error">
          Rejected{row.rejectedByName ? ` by ${row.rejectedByName}` : ""}.
          Reason: {row.rejectReason}
        </div>
      )}
      {row.status === "APPROVED" && (
        <div className="accounts-note ok">
          Posted as {row.voucherNo}
          {row.approvedByName ? `, approved by ${row.approvedByName}` : ""}
          {row.approvedAt ? ` on ${formatDate(String(row.approvedAt))}` : ""}.
        </div>
      )}
      {row.status === "CANCELLED" && (
        <div className="accounts-error">
          Voucher {row.voucherNo} was cancelled in Accounts.
        </div>
      )}
      {deciding && (
        <ApprovalPanel
          row={row}
          onClose={() => setDeciding(false)}
          onDone={() => {
            setDeciding(false);
            load();
          }}
        />
      )}
      <div className="accounts-entry">
        <CollapsibleSection title="Details">
          <div className="accounts-panel">
            <p>
              <span className={`accounts-chip ${TONE[row.status] || "muted"}`}>
                {row.statusLabel}
              </span>
            </p>
            <div className="accounts-facts">
              <div>
                <span className="accounts-hint">Voucher No</span>
                <strong>{row.voucherNo || "Given when approved"}</strong>
              </div>
              <div>
                <span className="accounts-hint">Entry No</span>
                <strong>{row.requestNo}</strong>
              </div>
              <div>
                <span className="accounts-hint">Date</span>
                <strong>{formatDate(row.voucherDate)}</strong>
              </div>
              <div>
                <span className="accounts-hint">
                  {row.voucherType === "PAYMENT"
                    ? "Payment Mode"
                    : "Receipt Mode"}
                </span>
                <strong>{row.paymentMode}</strong>
              </div>
              <div>
                <span className="accounts-hint">Reference No</span>
                <strong>{row.referenceNo || "—"}</strong>
              </div>
              <div>
                <span className="accounts-hint">Party</span>
                <strong>{row.partyName}</strong>
              </div>
              <div>
                <span className="accounts-hint">Entered by</span>
                <strong>{row.submittedByName || "—"}</strong>
              </div>
              <div>
                <span className="accounts-hint">
                  {row.status === "REJECTED" ? "Rejected by" : "Approved by"}
                </span>
                <strong>
                  {(row.status === "REJECTED"
                    ? row.rejectedByName
                    : row.approvedByName) || "—"}
                </strong>
              </div>
            </div>
            {(row.allocations || []).length > 0 && (
              <AllocationDocs
                allocations={row.allocations}
                advance={row.advance}
                advanceUsed={row.advanceUsed}
              />
            )}
            {row.source && (
              <SourceDocs
                source={row.source}
                hideInvoice={(row.allocations || []).length > 0}
              />
            )}
          </div>
        </CollapsibleSection>
        <CollapsibleSection title="Accounting entry">
          <div className="accounts-panel">
            <EntryPreview lines={row.lines} />
            <p className="accounts-narration">
              <span className="accounts-hint">Narration</span>
              {row.narration || "—"}
            </p>
          </div>
        </CollapsibleSection>
      </div>
    </PageShell>
  );
}
